// Minimal OpenID Connect Authorization Code + PKCE client for Keycloak.
// No external dependency — uses the browser's Web Crypto (works on localhost,
// which browsers treat as a secure context). Public SPA client, so no secret.
import cfg from "../config.js";

const kc = (cfg && cfg.keycloak) || {};
const REALM_BASE = `${kc.url}/realms/${kc.realm}/protocol/openid-connect`;
const REDIRECT_URI = `${window.location.origin}/oidc/callback`;

// ── PKCE / random helpers ────────────────────────────────────────────
function base64url(bytes) {
  let str = "";
  const b = new Uint8Array(bytes);
  for (let i = 0; i < b.length; i++) str += String.fromCharCode(b[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomString(len = 64) {
  const a = new Uint8Array(len);
  window.crypto.getRandomValues(a);
  return base64url(a).slice(0, len);
}
async function sha256(input) {
  const data = new TextEncoder().encode(input);
  return window.crypto.subtle.digest("SHA-256", data);
}

// Decode a JWT payload (no verification — the backend verifies; this is only
// for reading claims in the browser to show/route).
export function parseJwt(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(payload))));
  } catch (e) {
    return {};
  }
}

// ── Step 1: redirect the browser to Keycloak's login page ────────────
// idpHint (optional): a Keycloak identity-provider alias (e.g. "google"). When set,
// Keycloak skips its own login screen and goes straight to that provider via kc_idp_hint.
export async function startLogin(idpHint) {
  const verifier = randomString(64);
  const challenge = base64url(await sha256(verifier));
  const state = randomString(32);
  sessionStorage.setItem("kc_pkce_verifier", verifier);
  sessionStorage.setItem("kc_oauth_state", state);

  const params = new URLSearchParams({
    client_id: kc.clientId,
    redirect_uri: REDIRECT_URI,
    response_type: "code",
    // 'organization' asks Keycloak to include the user's Organization membership in the
    // token/userinfo (needs the 'organization' client scope assigned to intelog-frontend).
    scope: "openid profile email organization",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  if (idpHint) params.set("kc_idp_hint", idpHint);
  window.location.assign(`${REALM_BASE}/auth?${params.toString()}`);
}

// ── Step 2: on /oidc/callback, exchange the code for tokens ──────────
export async function handleCallback() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const err = url.searchParams.get("error");
  if (err) throw new Error(url.searchParams.get("error_description") || err);
  if (!code) throw new Error("No authorization code in callback URL");
  if (state !== sessionStorage.getItem("kc_oauth_state")) {
    throw new Error("OAuth state mismatch — possible CSRF, aborting");
  }
  const verifier = sessionStorage.getItem("kc_pkce_verifier");
  if (!verifier) throw new Error("Missing PKCE verifier — restart login");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: kc.clientId,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });
  const res = await fetch(`${REALM_BASE}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Token exchange failed (${res.status}): ${t}`);
  }
  const tokens = await res.json(); // { access_token, id_token, refresh_token, ... }
  sessionStorage.removeItem("kc_pkce_verifier");
  sessionStorage.removeItem("kc_oauth_state");
  return {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token,
    claims: parseJwt(tokens.access_token),
  };
}

// ── Silent refresh ───────────────────────────────────────────────────
// Keep the session alive without a full-page redirect: use the stored Keycloak
// refresh_token to get a fresh Keycloak access_token, then re-exchange it at
// /api/auth/keycloak for a fresh Intelog JWT. Returns true on success, false if it
// couldn't refresh (no/expired refresh token, Keycloak SSO ended, backend rejected) —
// the caller then falls back to a full re-login. Never throws.
export async function refreshSession() {
  let refreshToken = null;
  try { refreshToken = localStorage.getItem("kc_refresh_token"); } catch (e) { /* ignore */ }
  if (!refreshToken) return false;

  // 1) Keycloak refresh_token grant → new KC tokens.
  let kcTokens;
  try {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: kc.clientId,
      refresh_token: refreshToken,
    });
    const r = await fetch(`${REALM_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!r.ok) return false;
    kcTokens = await r.json(); // { access_token, id_token, refresh_token, ... }
  } catch (e) { return false; }
  if (!kcTokens || !kcTokens.access_token) return false;

  // 2) Re-exchange the fresh Keycloak access token for a fresh Intelog JWT.
  try {
    const res = await fetch(`${cfg.domain}/api/auth/keycloak`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${kcTokens.access_token}` },
      body: "{}",
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data.accessToken) return false;
    localStorage.setItem("token", data.accessToken);
    if (kcTokens.id_token) localStorage.setItem("kc_id_token", kcTokens.id_token);
    if (kcTokens.refresh_token) localStorage.setItem("kc_refresh_token", kcTokens.refresh_token);
    return true;
  } catch (e) { return false; }
}

// Milliseconds until the current Intelog JWT expires (from its `exp` claim), or null.
export function tokenMsRemaining() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    const { exp } = parseJwt(t);
    if (!exp) return null;
    return exp * 1000 - Date.now();
  } catch (e) { return null; }
}

// ── Logout: end the Keycloak SSO session too ─────────────────────────
export function logout(idToken) {
  const params = new URLSearchParams({
    post_logout_redirect_uri: `${window.location.origin}/`,
    client_id: kc.clientId,
  });
  if (idToken) params.set("id_token_hint", idToken);
  window.location.assign(`${REALM_BASE}/logout?${params.toString()}`);
}

// Clear the Intelog session, and if the user signed in via Keycloak, also end the
// Keycloak SSO session (otherwise "Sign in with Keycloak" logs straight back in).
// Falls back to a normal redirect to "/" for password logins.
export async function fullLogout() {
  // Detect a Keycloak login by the presence of kc_id_token. We rely on this rather
  // than user.authProvider because the caller may clear localStorage.user first
  // (PermissionProvider.logout does), and kc_id_token survives until we remove it here.
  let idToken = null;
  let token = null;
  try { idToken = localStorage.getItem("kc_id_token"); } catch (e) { /* ignore */ }
  try { token = localStorage.getItem("token"); } catch (e) { /* ignore */ }

  // Server-side revocation: denylist this token before we drop it, so a copy taken
  // before logout can't keep being used until its 24h expiry. Best-effort — logout
  // must still proceed (clear storage, end SSO) even if the backend is unreachable.
  if (token) {
    try {
      await fetch(`${cfg.ENDPOINT || cfg.domain}/api/auth/logout`, {
        method: "POST",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: "{}",
      });
    } catch (e) { /* ignore — proceed with client-side logout regardless */ }
  }

  try {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("kc_id_token");
    localStorage.removeItem("kc_refresh_token");
    localStorage.removeItem("langraph_session_id");
  } catch (e) { /* ignore */ }
  if (idToken) {
    logout(idToken); // Keycloak login → end the SSO session, then back to "/"
  } else {
    window.location.href = "/";
  }
}
